import { DialogComponent } from '@theme/dialog';

/**
 *
 * @extends DialogComponent
 */
class ThemeVideoModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:open', this.#mountVideo);
    this.addEventListener('dialog:close', this.#unmountVideo);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#mountVideo);
    this.removeEventListener('dialog:close', this.#unmountVideo);
  }

  /** @returns {Element | null} */
  get #container() {
    return this.querySelector('[data-video-target]');
  }

  #mountVideo = () => {
    // Hide the fixed site header so it doesn't overlap the modal / close button.
    document.documentElement.classList.add('theme-video-modal-open');

    const container = this.#container;
    if (!container) {
      return;
    }
    if (container.firstElementChild) {
      return;
    }

    const url = this.getAttribute('data-video-url') || '';
    const title = this.getAttribute('data-video-title') || 'Video';
    const embed = this.#getEmbedSrc(url);

    if (embed) {
      const iframe = document.createElement('iframe');
      iframe.className = 'theme-video-modal__iframe';
      iframe.src = embed;
      iframe.title = title;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      container.appendChild(iframe);
      return;
    }

    if (url) {
      // Direct video file (for example a Shopify uploaded MP4) plays natively.
      const video = document.createElement('video');
      video.className = 'theme-video-modal__video';
      video.preload = 'metadata';
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;

      const source = document.createElement('source');
      source.src = url;
      source.type = 'video/mp4';
      video.appendChild(source);

      container.appendChild(video);
      video.play().catch(() => {});
    }
  };

  #unmountVideo = () => {
    document.documentElement.classList.remove('theme-video-modal-open');
    this.#container?.replaceChildren();
  };

  /**
   * Builds an autoplay embed URL for YouTube/Vimeo, or returns null for anything
   * that should be treated as a direct video file.
   *
   * @param {string} url
   * @returns {string | null}
   */
  #getEmbedSrc(url) {
    if (!url) return null;

    /** @type {URL | undefined} */
    let parsed;
    let host = '';
    try {
      parsed = new URL(url, window.location.origin);
      host = parsed.hostname;
    } catch (_error) {
      host = '';
    }

    const youTubeId = this.#getYouTubeId(url, parsed);
    if (youTubeId) {
      return `https://www.youtube-nocookie.com/embed/${youTubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    }

    if (host.includes('vimeo.com')) {
      const vimeoId = (parsed?.pathname || '').split('/').filter(Boolean).pop();
      if (vimeoId && /^\d+$/.test(vimeoId)) {
        return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
      }
    }

    // Already a player/embed URL - use as-is.
    if (host.includes('youtube') || host.includes('player.vimeo')) {
      return url;
    }

    // Any other URL is treated as a direct media file and handled by <video>.
    return null;
  }

  /**
   * @param {string} url
   * @param {URL | undefined} parsed
   * @returns {string}
   */
  #getYouTubeId(url, parsed) {
    if (!parsed) return '';

    const host = parsed.hostname.toLowerCase();
    const isYouTubeHost =
      host.includes('youtube.com') ||
      host.includes('youtube-nocookie.com') ||
      host.includes('youtu.be');

    if (!isYouTubeHost) {
      return '';
    }

    if (host.includes('youtu.be')) {
      return parsed.pathname.replace('/', '').split('/')[0] ?? '';
    }

    if (parsed.searchParams.has('v')) {
      return parsed.searchParams.get('v') || '';
    }

    const embedMatch = parsed.pathname.match(/\/embed\/([^/?]+)/);
    if (embedMatch) return embedMatch[1] ?? '';

    const shortsMatch = parsed.pathname.match(/\/shorts\/([^/?]+)/);
    if (shortsMatch) return shortsMatch[1] ?? '';

    return '';
  }
}

if (!customElements.get('theme-video-modal')) {
  customElements.define('theme-video-modal', ThemeVideoModal);
}
